import { formatAuthError } from '@features/auth'
import type { User } from '@features/auth/auth-client-adapter'
import { Update, defModule, type UpdateEffect, type TaskMessage, type UpdateResult } from '@st/redux'
import type { Organization } from '@st/sdk'
import { isNotEmpty } from '@st/util/json-value'
import { readUTMCodesFromCookieHeader } from '@st/util/utm-tracking'
import { decodeSchemaFields } from '@util/decode-schema-fields'
import { Effect, Result } from 'effect'
import { match, P } from '@st/pattern'
import { Analytics, Auth, BrowserCookies, Routing, SdkClient } from '~/effects'
import { observeAuth, runSdkRequest } from '~/features/module-runtime'
import { routes } from '~/features/routes'
import {
  type AccountCreationInput,
  type OrganizationCreationInput,
  type OrganizationSignUpFieldErrors,
  type OrganizationSignUpInput,
  organizationSignUpFlow,
  toOrganizationCreationInput,
  type SignUpFlowId
} from './organization-sign-up-flow'

type SignUpError =
  | { type: 'validation'; errors: OrganizationSignUpFieldErrors }
  | { type: 'general'; message: string }

export type OrganizationSignUpState =
  | { status: 'resolvingAuth' }
  | { status: 'enteringAccountDetails'; error?: SignUpError }
  | { status: 'selectingOrganization'; user: User }
  | { status: 'enteringOrganizationDetails'; user: User; error?: SignUpError }
  | { status: 'creatingUser'; organization: OrganizationCreationInput; flow?: SignUpFlowId }
  | { status: 'creatingOrganization'; user: User; flow?: SignUpFlowId }
  | { status: 'finishing'; user: User; flow?: SignUpFlowId }

const observeSignUpAuth = observeAuth((event): OrganizationSignUpAuthChanged | null =>
  match(event)
    .returnType<OrganizationSignUpAuthChanged | null>()
    .with({ type: 'loggedIn' }, ({ user }) => ({
      type: 'currentUserChanged',
      user
    }))
    .with({ type: 'loggedOut' }, () => ({
      type: 'currentUserChanged',
      user: null
    }))
    .with({ type: 'loginFailed' }, () => null)
    .exhaustive()
)

type OrganizationSignUpAuthChanged = {
  type: 'currentUserChanged'
  user: User | null
}

type AuthObservedSignUpState = Extract<
  OrganizationSignUpState,
  {
    status:
      | 'resolvingAuth'
      | 'enteringAccountDetails'
      | 'selectingOrganization'
      | 'enteringOrganizationDetails'
  }
>

export type OrganizationSignUpMessage =
  | TaskMessage<typeof observeSignUpAuth>
  | { type: 'createOrganizationSelected' }
  | { type: 'submit'; input: OrganizationSignUpInput; termsRequired: boolean; flow?: SignUpFlowId }
  | { type: 'userCreated'; user: User }
  | { type: 'userCreationFailed'; error: string }
  | { type: 'organizationCreated'; organization: Organization }
  | { type: 'organizationCreationFailed'; error: string }
  | { type: 'finished' }

/**
 * Organization sign-up owns both the screen and the creation workflow:
 *
 * resolvingAuth --loggedOut--> enteringAccountDetails
 *       |--loggedIn--> selectingOrganization --create--> enteringOrganizationDetails
 *
 * enteringAccountDetails ------submit------> creatingUser --userCreated--+
 * enteringOrganizationDetails --submit-----------------------------------+-->
 *                                                   creatingOrganization --> finishing
 *
 * A failed step returns to the form that can actually recover it. In
 * particular, organization failure retains the authenticated user and never
 * falls back through account entry or organization selection.
 *
 * Auth is a state-owned observation, not a second owner of the workflow. It
 * selects the passive entry screen, then stops while Commands advance an
 * active submission. Returning to a form restarts the observation and replays
 * the authoritative current identity. Credentials remain transition input and
 * never enter durable state.
 */

export const organizationSignUpModule = defModule<{
  state: OrganizationSignUpState
  message: OrganizationSignUpMessage
}>({
  init: () => ({ status: 'resolvingAuth' }),
  tasks: organizationSignUpTasks,
  update: updateOrganizationSignUp
})

function organizationSignUpTasks(state: OrganizationSignUpState) {
  return match(state)
    .with({ status: P.union('creatingUser', 'creatingOrganization', 'finishing') }, () => [])
    .with(
      {
        status: P.union(
          'resolvingAuth',
          'enteringAccountDetails',
          'selectingOrganization',
          'enteringOrganizationDetails'
        )
      },
      () => [observeSignUpAuth()]
    )
    .exhaustive()
}

function updateOrganizationSignUp(
  state: OrganizationSignUpState,
  message: OrganizationSignUpMessage
): UpdateResult<OrganizationSignUpState, OrganizationSignUpMessage> {
  const update = match({ state, message })
    .returnType<UpdateResult<OrganizationSignUpState, OrganizationSignUpMessage>>()
    .with(
      {
        state: {
          status: P.union(
            'resolvingAuth',
            'enteringAccountDetails',
            'selectingOrganization',
            'enteringOrganizationDetails'
          )
        },
        message: { type: 'currentUserChanged' }
      },
      ({ state, message }) => updateCurrentUser(state, message.user)
    )
    .with(
      {
        state: { status: 'selectingOrganization' },
        message: { type: 'createOrganizationSelected' }
      },
      ({ state }) => ({ status: 'enteringOrganizationDetails', user: state.user })
    )
    .with(
      { state: { status: 'enteringAccountDetails' }, message: { type: 'submit' } },
      ({ message }) => {
        const validation = decodeSchemaFields(
          organizationSignUpFlow(message.flow).forms(message.termsRequired).loggedOutSchema,
          message.input
        )
        if (Result.isFailure(validation)) {
          return {
            status: 'enteringAccountDetails',
            error: { type: 'validation', errors: validation.failure }
          }
        }

        const organization = toOrganizationCreationInput(validation.success)

        return [
          {
            status: 'creatingUser',
            organization,
            flow: message.flow
          },
          createUser(validation.success)
        ]
      }
    )
    .with(
      { state: { status: 'enteringOrganizationDetails' }, message: { type: 'submit' } },
      ({ state, message }) => {
        const validation = decodeSchemaFields(
          organizationSignUpFlow(message.flow).forms(message.termsRequired).loggedInSchema,
          message.input
        )
        if (Result.isFailure(validation)) {
          return {
            status: 'enteringOrganizationDetails',
            user: state.user,
            error: { type: 'validation', errors: validation.failure }
          }
        }

        return [
          { status: 'creatingOrganization', user: state.user, flow: message.flow },
          createOrganization(toOrganizationCreationInput(validation.success), state.user)
        ]
      }
    )
    .with(
      { state: { status: 'creatingUser' }, message: { type: 'userCreated' } },
      ({ state, message }) => [
        { status: 'creatingOrganization', user: message.user, flow: state.flow },
        createOrganization(state.organization, message.user)
      ]
    )
    .with(
      { state: { status: 'creatingUser' }, message: { type: 'userCreationFailed' } },
      ({ message }) => ({
        status: 'enteringAccountDetails',
        error: { type: 'validation', errors: { userEmail: message.error } }
      })
    )
    .with(
      {
        state: { status: 'creatingOrganization' },
        message: { type: 'organizationCreated' }
      },
      ({ state, message }) => [
        { status: 'finishing', user: state.user, flow: state.flow },
        finishSignUp(message.organization, state.flow)
      ]
    )
    .with(
      {
        state: { status: 'creatingOrganization' },
        message: { type: 'organizationCreationFailed' }
      },
      ({ state, message }) => ({
        status: 'enteringOrganizationDetails',
        user: state.user,
        error: { type: 'general', message: message.error }
      })
    )
    .with({ state: { status: 'finishing' }, message: { type: 'finished' } }, ({ state }) => ({
      status: 'selectingOrganization',
      user: state.user
    }))
    .otherwise(({ state, message }) => unexpectedMessage(state, message))

  return match(message)
    .with({ type: 'submit' }, () =>
      Update.pipe(
        update,
        Update.effect(() => trackSignUpClick())
      )
    )
    .otherwise(() => update)
}

function trackSignUpClick() {
  return Effect.gen(function* () {
    const analytics = yield* Analytics
    yield* analytics.track('clicked_sign_up')
    return null
  })
}

function updateCurrentUser(
  state: AuthObservedSignUpState,
  user: User | null
): OrganizationSignUpState {
  return match(user)
    .returnType<OrganizationSignUpState>()
    .with(null, () =>
      match(state)
        .with({ status: 'enteringAccountDetails' }, (state) => state)
        .otherwise(() => ({ status: 'enteringAccountDetails' }))
    )
    .with(P.nonNullable, (user) =>
      match(state)
        .returnType<OrganizationSignUpState>()
        .with({ status: 'enteringOrganizationDetails' }, (state) => ({ ...state, user }))
        .with(
          {
            status: P.union('resolvingAuth', 'enteringAccountDetails', 'selectingOrganization')
          },
          () => ({ status: 'selectingOrganization', user })
        )
        .exhaustive()
    )
    .exhaustive()
}

export function organizationSignUpOwnsAuthScreen(state: OrganizationSignUpState): boolean {
  return match(state)
    .with(
      {
        status: P.union(
          'enteringOrganizationDetails',
          'creatingUser',
          'creatingOrganization',
          'finishing'
        )
      },
      () => true
    )
    .with(
      { status: P.union('resolvingAuth', 'enteringAccountDetails', 'selectingOrganization') },
      () => false
    )
    .exhaustive()
}

function createUser(input: AccountCreationInput): UpdateEffect<OrganizationSignUpMessage> {
  return Effect.gen(function* () {
    const auth = yield* Auth
    return yield* auth
      .signUp({
        name: input.userName,
        email: input.userEmail,
        password: input.password,
        passwordConfirm: input.passwordConfirm
      })
      .pipe(
        Effect.map((user) => ({ type: 'userCreated', user }) as const),
        Effect.catchTag('AuthFailure', (error) =>
          Effect.succeed({ type: 'userCreationFailed', error: formatAuthError(error) } as const)
        )
      )
  })
}

function createOrganization(
  input: OrganizationCreationInput,
  user: User
): UpdateEffect<OrganizationSignUpMessage> {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const cookies = yield* BrowserCookies
    const cookieHeader = yield* cookies.read
    const utmCodes = yield* readUTMCodesFromCookieHeader(cookieHeader, 'st_utm')
    const result = yield* runSdkRequest(
      sdk.send({
        type: 'organizations/createOrganization',
        name: input.organizationName,
        taxApp: input.taxApp,
        practiceManagementApp: input.practiceManagementApp,
        phoneNumber: isNotEmpty(input.phoneNumber) ? input.phoneNumber : undefined,
        numberOfClients: input.numberOfClients,
        email: isNotEmpty(input.userEmail) ? input.userEmail : user.email,
        utmCodes
      })
    )
    if (!result.ok) {
      return {
        type: 'organizationCreationFailed',
        error: organizationCreationErrorMessage(result.error)
      } as const
    }
    return { type: 'organizationCreated', organization: result.response } as const
  })
}

function finishSignUp(
  organization: Organization,
  flow: SignUpFlowId | undefined
): UpdateEffect<OrganizationSignUpMessage> {
  return Effect.gen(function* () {
    const analytics = yield* Analytics
    const routing = yield* Routing

    yield* analytics.track('signed_up')

    const currentLocation = yield* routing.currentLocation
    const route = routes.match(currentLocation.pathname) ?? null
    if (route?.name == 'sign_up') {
      yield* routing.navigate(organizationSignUpFlow(flow).completionRoute(organization.slug))
    }

    return { type: 'finished' } as const
  })
}

function organizationCreationErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  return 'An unknown error occurred'
}

function unexpectedMessage(
  state: OrganizationSignUpState,
  message: OrganizationSignUpMessage
): never {
  throw new Error(`Unexpected organization-sign-up message ${message.type} while ${state.status}`)
}

export const ORGANIZATION_NUM_CLIENTS_OPTIONS = [
  '0-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-2000',
  '2001-5000',
  '5000+'
]

type Option = {
  key: string
  label: string
}
export const PRACTICE_MANAGEMENT_APP_OPTIONS: Option[] = [
  { key: 'karbon', label: 'Karbon' },
  { key: 'canopy', label: 'Canopy' },
  { key: 'tax_dome', label: 'TaxDome' },
  { key: 'financial_cents', label: 'Financial Cents' },
  { key: 'client_hub', label: 'Client Hub' },
  { key: 'firm360', label: 'Firm360' },
  { key: 'practice_cs', label: 'Practice CS' },
  { key: 'onvio', label: 'Onvio' },
  { key: 'cch_ifirm_practice_manager', label: 'CCH iFirm Practice Manager' },
  { key: 'cch_axcess_practice', label: 'CCH Axcess Practice' },
  { key: 'jetpack_workflow', label: 'Jetpack Workflow' },
  { key: 'qount', label: 'Qount' },
  { key: 'uku', label: 'Uku' },
  { key: 'aiwyn', label: 'Aiwyn' },
  { key: 'no_pm', label: "I don't use a PM" },
  { key: 'other_pm', label: 'Other PM' }
]
